import { recordDestination, recordNeighbor } from './recordDestination';
import { useDemandMatrixPage } from '@/api/rpc/hooks/procurement/useDemandForecast';
import {
  DEMAND_PAGE_SIZE,
  listSearchOf,
  toDemandMatrixInput,
  type DemandDetailSearch,
} from '../demand/demandFilters';

export function useRecordNavigation(
  search: DemandDetailSearch & { rowIndex: number },
) {
  const page = Math.floor(search.rowIndex / DEMAND_PAGE_SIZE) + 1;
  const list = listSearchOf(search);
  const input = toDemandMatrixInput({
    ...list,
    runId: search.runId ?? search.navigationRunId,
  });
  const current = useDemandMatrixPage({ ...input, page }, true);
  const rows = current.data?.rows ?? [];
  // Validate identity after refetches: an override can change a quantity sort.
  // Never trust the old offset to identify the item now occupying that row.
  // The cursor's location is the one the page is filtered to; a wider filter
  // has no single row to sit on, so nothing matches and navigation goes quiet.
  const cursorWarehouseId = search.warehouseId;
  const index = rows.findIndex(
    (row) => row.sku === search.sku && row.warehouseId === cursorWarehouseId,
  );
  const needsPreviousPage = index === 0 && page > 1;
  const needsNextPage =
    index >= 0 && index === rows.length - 1 && current.data?.hasMore === true;
  const previous = useDemandMatrixPage(
    { ...input, page: Math.max(1, page - 1) },
    needsPreviousPage,
  );
  const next = useDemandMatrixPage({ ...input, page: page + 1 }, needsNextPage);
  const previousRow = recordNeighbor(
    rows,
    index,
    -1,
    needsPreviousPage ? previous.data?.rows : undefined,
  );
  const nextRow = recordNeighbor(
    rows,
    index,
    1,
    needsNextPage ? next.data?.rows : undefined,
  );
  const activeQueries = [current];
  if (needsPreviousPage) activeQueries.push(previous);
  if (needsNextPage) activeQueries.push(next);
  const busy = activeQueries.some((query) => query.isFetching);
  const failedQueries = activeQueries.filter((query) => query.isError);
  const failed = failedQueries.length > 0;
  const unavailable = busy || failed || index < 0;
  const offset = (page - 1) * DEMAND_PAGE_SIZE + index;
  const previousTarget = recordDestination(
    previousRow,
    offset - 1,
    list,
    current.data?.runId,
    search.navigationRunId,
  );
  const nextTarget = recordDestination(
    nextRow,
    offset + 1,
    list,
    current.data?.runId,
    search.navigationRunId,
  );

  return {
    index,
    offset,
    total: current.data?.totalRows ?? null,
    busy,
    failed,
    unavailable,
    previousTarget,
    nextTarget,
    retry: () => {
      for (const query of failedQueries) void query.refetch();
    },
  };
}
